import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabaseClient';

// Landing page for email-confirmation links (/auth/confirm?token_hash=...&type=...).
// The Supabase "Confirm signup" and "Change Email Address" templates link here (see
// supabase/templates/) so the URL in the email lives on collectionkeeper.org instead
// of *.supabase.co — the token is exchanged client-side via verifyOtp.
//
// type=email_change uses Secure Email Change: links go to BOTH the old and new
// address and the change completes on the second click. Characterized on staging
// 2026-08-18: the first click (either order) returns success with no session and
// an empty user; the completing click returns a session whose user carries the
// new email. That distinction drives the 'partial' vs 'success' branch below.
export default function AuthConfirmPage() {
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'partial' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [linkType] = useState(() => new URLSearchParams(window.location.search).get('type') || 'signup');
  // One-shot guard: the token is single-use, so the verify must fire exactly
  // once even when StrictMode (dev) re-runs the mount effect — the second run
  // would consume-then-reject the token and paint a false LINK PROBLEM.
  const verifyStartedRef = useRef(false);

  useEffect(() => {
    if (verifyStartedRef.current) return;
    verifyStartedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type') || 'signup';
    if (!tokenHash) {
      setStatus('error');
      setErrorMsg('This link is missing its confirmation token. Open the link directly from the email we sent you.');
      return;
    }
    supabase.auth.verifyOtp({ token_hash: tokenHash, type }).then(async ({ data, error }) => {
      if (error) {
        setStatus('error');
        setErrorMsg(type === 'email_change'
          ? 'This confirmation link is invalid or has expired. Each link is single-use — if you already clicked both, your email is updated and you can just sign in. If the links lapsed, start the change again from My Profile.'
          : 'This confirmation link is invalid or has expired. Links are single-use — if you already clicked it, just sign in.');
        return;
      }
      if (type === 'email_change') {
        // Secure email change completes on the SECOND of two clicks. The verify
        // response alone can't tell first from second when this browser already
        // holds a session (it echoes that session either way), so ask the server
        // whether a change is still pending. Signed-out first click: verifyOtp
        // stores no session, getUser fails -> partial. Signed-out second click:
        // verifyOtp stored the fresh session -> getUser shows new_email cleared.
        const { data: fresh } = await supabase.auth.getUser();
        const stillPending = fresh?.user ? !!fresh.user.new_email : !data?.session;
        if (stillPending) {
          setStatus('partial'); // the other address still has to click
          return;
        }
      }
      setStatus('success');
      setTimeout(() => window.location.replace('/'), 1800);
    });
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <img src="/logo.png" style={{ width: '72px', height: '72px', borderRadius: '12px' }} alt="CK" />
        </div>
        <h1 style={styles.title}>
          COLLECTION<span style={styles.titleAccent}>KEEPER</span>
        </h1>

        {status === 'verifying' && (
          <>
            <div style={styles.statusLine}>// VERIFYING YOUR EMAIL //</div>
            <p style={styles.text}>Hang tight — confirming {linkType === 'email_change' ? 'your email change' : 'your account'}…</p>
          </>
        )}

        {status === 'success' && linkType === 'email_change' && (
          <>
            <div style={{ ...styles.statusLine, color: 'var(--green)' }}>// EMAIL UPDATED //</div>
            <p style={styles.text}>
              Both confirmations are in — your account email is updated.
              Use the new address next time you sign in. Taking you to your collection…
            </p>
          </>
        )}

        {status === 'success' && linkType !== 'email_change' && (
          <>
            <div style={{ ...styles.statusLine, color: 'var(--green)' }}>// EMAIL CONFIRMED //</div>
            <p style={styles.text}>Your account is ready. Taking you to your collection…</p>
          </>
        )}

        {status === 'partial' && (
          <>
            <div style={{ ...styles.statusLine, color: 'var(--gold)' }}>// ONE MORE STEP //</div>
            <p style={styles.text}>
              Confirmation received. To finish changing your account email, click the link
              in the email we sent to your other address.
            </p>
            <p style={styles.hint}>
              For security, both your old and your new address have to approve the change.
              Nothing changes until the second link is clicked.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ ...styles.statusLine, color: 'var(--red)' }}>// LINK PROBLEM //</div>
            <p style={styles.text}>{errorMsg}</p>
            <button style={styles.btn} onClick={() => window.location.replace('/')}>
              GO TO SIGN IN
            </button>
            <p style={styles.hint}>
              Not confirmed yet? Sign in with your email and password and we&apos;ll offer to resend the link.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Rajdhani', sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    background: 'var(--panel-deep)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    padding: '40px',
    textAlign: 'center',
    boxShadow: '0 0 60px rgba(var(--cyan-rgb),0.06), 0 0 120px rgba(0,0,0,0.8)',
  },
  title: {
    fontFamily: "'Orbitron', monospace",
    fontSize: 'clamp(16px, 5vw, 28px)',
    fontWeight: 900,
    color: 'var(--text-strong)',
    lineHeight: 1.1,
    margin: 0,
  },
  titleAccent: { color: 'var(--gold)' },
  statusLine: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--cyan)',
    letterSpacing: '3px',
    marginTop: '18px',
  },
  text: {
    fontFamily: "'Rajdhani', sans-serif",
    fontSize: '15px',
    color: 'var(--text)',
    lineHeight: 1.5,
    marginTop: '14px',
  },
  btn: {
    width: '100%',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px',
    letterSpacing: '2px',
    padding: '13px',
    background: 'linear-gradient(135deg, rgba(var(--cyan-rgb),0.15), rgba(var(--brand-gold-rgb),0.1))',
    border: '1px solid var(--cyan)',
    color: 'var(--cyan)',
    cursor: 'pointer',
    borderRadius: '2px',
    marginTop: '18px',
  },
  hint: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: 'var(--muted)',
    lineHeight: 1.6,
    marginTop: '14px',
  },
};
